import React, { useEffect, useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import useParams from 'core/hooks/useParams'
import { isNilOrEmpty, switchCase } from 'utils/fp'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import { keys, omit, without } from 'ramda'
import {
  updateVirtualMachine,
  updateVirtualMachineInstance,
} from 'app/plugins/kubevirt/components/virtual-machines/new-actions'
import TextField from 'core/components/validatedForm/TextField'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { updatePod } from 'k8s/components/pods/new-actions'
import { updateNetworkPlugin } from 'k8s/components/networking/plugins/actions'
import { updateDataVolume } from 'k8s/components/storage/data-volumes/new-actions'
import { updatePersistentVolume } from 'k8s/components/storage/persistent-volume/new-actions'
import { updateConfigMap } from 'k8s/components/config-maps/new-actions'
import { updateDeployment } from 'k8s/components/deployments/new-actions'
import { updateService } from 'k8s/components/services/new-actions'
import { updateStatefulSet } from 'k8s/components/stateful-sets/actions'
import { updateReplicaSet } from 'k8s/components/replica-sets/actions'
import { updateDaemonSet } from 'k8s/components/daemon-sets/actions'
import { updateJob } from 'k8s/components/cronjobs/actions'
import { updateSecret } from 'k8s/components/configuration/secrets/actions'
import { updateResourceQuota } from 'k8s/components/configuration/resource-quotas/actions'
import { updateLimitRange } from 'k8s/components/configuration/limit-ranges/actions'
import { updateHorizontalPodAutoscaler } from 'k8s/components/configuration/horizontal-pod-autoscaler/actions'
import { updatePodDisruptionBudget } from 'k8s/components/configuration/pod-disruption-budgets/actions'
import { updateK8sEndpoint } from 'k8s/components/network/endpoints/actions'
import { updateIngress } from 'k8s/components/ingresses/new-actions'
import { updateNetworkPolicy } from 'k8s/components/network/network-policies/actions'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    padding: '24px 40px',
    display: 'grid',
    gap: 32,
  },
  currentLabels: {
    display: 'grid',
    gap: 24,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
  },
  labelKey: {
    flexGrow: 1,
  },
  labelValue: {
    flexGrow: 1,
  },
  labelDash: {
    fontSize: 24,
    margin: `0px 8px`,
    position: 'relative',
    top: 10,
  },
  minusIcon: {
    color: theme.components.badge.primary.color,
    position: 'relative',
    top: 14,
    marginLeft: 24,
  },
  addLabels: {
    display: 'grid',
    gap: 24,
  },
  addLabel: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: 8,
  },
  plusIcon: {
    color: theme.components.badge.primary.color,
  },
  noneText: {
    margin: theme.spacing(2),
    color: theme.components.card.passiveText,
  },
}))

export default function EditEntityLabelsModal({ resourceType, entity, open, onClose }) {
  const classes = useStyles()
  const [loaded, setLoaded] = useState(false)
  const defaultParams = {
    resourceType,
    currentIndexes: [],
    labelKeys: {},
    labelValues: {},
    newIndexes: [],
    newLabelKeys: {},
    newLabelValues: {},
  }

  const { params, updateParams, setParams } = useParams<{
    resourceType: string
    currentIndexes?: number[]
    labelKeys?: { [key: string]: string }
    labelValues?: { [key: string]: string | number }
    newIndexes?: number[]
    newLabelKeys?: { [key: string]: string }
    newLabelValues?: { [key: string]: string | number }
  }>(defaultParams)

  const labels = entity?.labels || entity?.metadata?.labels

  useEffect(() => {
    // Prevent resetting of form from background reloads
    if (loaded) {
      return
    }
    if (labels) {
      const labelKeys = keys(labels) || []
      const labelDataMap = labelKeys.reduce(
        (accum, key, index) => ({
          indexes: [...accum.indexes, index],
          keys: {
            ...accum.keys,
            [index]: key,
          },
          values: {
            ...accum.values,
            [index]: labels?.[key],
          },
        }),
        { indexes: [], keys: {}, values: {} },
      )
      updateParams({
        currentIndexes: labelDataMap.indexes,
        labelKeys: labelDataMap.keys,
        labelValues: labelDataMap.values,
      })
      setLoaded(true)
    }
  }, [entity, loaded])

  const updateFn = switchCase({
    pod: updatePod,
    deployment: updateDeployment,
    service: updateService,
    statefulSet: updateStatefulSet,
    replicaSet: updateReplicaSet,
    daemonSet: updateDaemonSet,
    job: updateJob,
    vm: updateVirtualMachine,
    vmi: updateVirtualMachineInstance,
    networkPlugin: updateNetworkPlugin,
    dataVolume: updateDataVolume,
    persistentVolume: updatePersistentVolume,
    configMap: updateConfigMap,
    secret: updateSecret,
    resourceQuota: updateResourceQuota,
    limitRange: updateLimitRange,
    horizontalPodAutoscaler: updateHorizontalPodAutoscaler,
    podDisruptionBudget: updatePodDisruptionBudget,
    k8sEndpoint: updateK8sEndpoint,
    ingress: updateIngress,
    networkPolicy: updateNetworkPolicy,
  })(params.resourceType)

  const { update, updating, error, reset } = useUpdateAction(updateFn)

  const submitForm = async () => {
    const currentLabels = params.currentIndexes.reduce(
      (accum, index) => ({
        ...accum,
        [params.labelKeys[index]]: params.labelValues[index],
      }),
      {},
    )
    const newLabels = params.newIndexes.reduce(
      (accum, index) => ({
        ...accum,
        [params.newLabelKeys[index]]: params.newLabelValues[index],
      }),
      {},
    )
    const combinedLabels = {
      ...currentLabels,
      ...newLabels,
    }

    const ops = []
    ops.push({
      op: 'replace',
      path: '/metadata/labels',
      value: combinedLabels,
    })

    const { success } = await update({
      ...entity,
      body: ops,
      requestType: 'patch',
      contentType: 'application/json-patch+json',
    })
    if (success) handleClose()
  }

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  const removeCurrentLabel = useCallback(
    (idx) => {
      updateParams({
        currentIndexes: without([idx], params.currentIndexes),
        labelKeys: omit([idx], params.labelKeys),
        labelValues: omit([idx], params.labelValues),
      })
    },
    [params, updateParams],
  )

  const removeNewLabel = useCallback(
    (idx) => {
      updateParams({
        newIndexes: without([idx], params.newIndexes),
        newLabelKeys: omit([idx], params.newLabelKeys),
        newLabelValues: omit([idx], params.newLabelValues),
      })
    },
    [params, updateParams],
  )

  const addNewLabel = useCallback(() => {
    const newIndex = params?.newIndexes?.length
      ? params.newIndexes[params.newIndexes.length - 1] + 1
      : 0
    updateParams({
      newIndexes: [...params.newIndexes, newIndex],
    })
  }, [params, updateParams])

  return (
    <ModalForm
      open={open}
      title={`Edit Labels`}
      onSubmit={submitForm}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Update Labels`}
    >
      <div className={classes.container}>
        <FormFieldSection title="Current Labels">
          {!isNilOrEmpty(params.currentIndexes) ? (
            <div className={classes.currentLabels}>
              {params?.currentIndexes?.map((idx) => (
                <div key={idx} className={classes.labelRow}>
                  <div className={classes.labelKey}>
                    <TextField
                      id={`currentKey${idx}`}
                      label="Key"
                      onChange={(value) =>
                        updateParams({
                          labelKeys: {
                            ...params.labelKeys,
                            [idx]: value,
                          },
                        })
                      }
                      value={params?.labelKeys?.[idx]}
                      required
                    />
                  </div>
                  <span className={classes.labelDash}>-</span>
                  <div className={classes.labelValue}>
                    <TextField
                      id={`currentValue${idx}`}
                      label="Value"
                      onChange={(value) =>
                        updateParams({
                          labelValues: {
                            ...params.labelValues,
                            [idx]: value,
                          },
                        })
                      }
                      value={params?.labelValues?.[idx]}
                      required
                    />
                  </div>
                  <FontAwesomeIcon
                    className={classes.minusIcon}
                    onClick={() => removeCurrentLabel(idx)}
                    size="xl"
                    solid
                  >
                    circle-minus
                  </FontAwesomeIcon>
                </div>
              ))}
            </div>
          ) : (
            <Text className={classes.noneText} variant="caption1">
              No Labels
            </Text>
          )}
        </FormFieldSection>
        <FormFieldSection title="Add Labels">
          <div className={classes.addLabels}>
            {params?.newIndexes?.map((idx) => (
              <div key={idx} className={classes.labelRow}>
                <div className={classes.labelKey}>
                  <TextField
                    id={`newKey${idx}`}
                    label="Key"
                    onChange={(value) =>
                      updateParams({
                        newLabelKeys: {
                          ...params.newLabelKeys,
                          [idx]: value,
                        },
                      })
                    }
                    value={params?.newLabelKeys?.[idx]}
                    required
                  />
                </div>
                <span className={classes.labelDash}>-</span>
                <div className={classes.labelValue}>
                  <TextField
                    id={`newValue${idx}`}
                    label="Value"
                    onChange={(value) =>
                      updateParams({
                        newLabelValues: {
                          ...params.newLabelValues,
                          [idx]: value,
                        },
                      })
                    }
                    value={params?.newLabelValues?.[idx]}
                    required
                  />
                </div>
                <FontAwesomeIcon
                  className={classes.minusIcon}
                  onClick={() => removeNewLabel(idx)}
                  size="xl"
                  solid
                >
                  circle-minus
                </FontAwesomeIcon>
              </div>
            ))}
            <div className={classes.addLabel} onClick={addNewLabel}>
              <FontAwesomeIcon className={classes.plusIcon} size="xl" solid>
                circle-plus
              </FontAwesomeIcon>
              <Text variant="body2">Add Label</Text>
            </div>
          </div>
        </FormFieldSection>
      </div>
    </ModalForm>
  )
}
